import * as React from "react";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { useMemo } from "react";
import {
  CellContext,
  ColumnDef
} from "@tanstack/react-table";

import { FilterParams } from "src/library/utils";
import { ActionButton } from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Modal } from "src/library/components/Modal";
import Table, { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PaginatedParams } from "src/core/shared/api";
import { PageProps } from "src/pages/Router/routes";
import {
  useShutdownsApi,
  IShutdown
} from "src/modules/shutdowns/api/shutdown.api";
import { shutdownsTableColumns } from "src/core/shared/components/tables/shutdownsTableColumns";
import { useDrop } from "src/core/drops/hooks";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { useCurrentUser } from "src/core/users/hooks";
import { CreateShutdownContext } from "src/modules/shutdowns/contexts/CreateShutdownContext";

const defaultPagination: PaginatedParams<IShutdown> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "startDate", "DESC" ] ]
};

interface UrlParams {
  dropId?: string;
  customerId?: string;
}

/**
 * View Shutdowns for a drop
 *
 */
const DropShutdownsPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { history, match } = props;
  const [ { drop } ] = useDrop();
  const [ { currentRole } ] = useCurrentUser();
  const TableRef = React.useRef<TableRefProps>(null);
  const [ shutdownsResponse, getShutdowns ] = useShutdownsApi("LIST_DROP_SHUTDOWNS");
  const [ userAccessibleShutdowns, getUserAccessibleShutdowns ] = useShutdownsApi("LIST_USER_ACCESSIBLE_SHUTDOWNS");
  const shutdownsData = useMemo(() => shutdownsResponse.data?.result || userAccessibleShutdowns.data?.result || [], [ shutdownsResponse.data?.result, userAccessibleShutdowns.data?.result ]);
  const [ deleteShutdownResponse, deleteShutdown ] = useShutdownsApi("DELETE_SHUTDOWN");
  const [ modalData, setModalData ] = React.useState<number | null>(null);
  const checkPermission = useCheckUserPermission();
  // use the amend order permission for the time being until we have shutdown specific permissions
  const canCreate = checkPermission("amend-order");
  const canAmend = checkPermission("amend-order");
  const canDelete = checkPermission("amend-order");
  const dropId = React.useMemo(() => drop?.id, [ drop?.id ]);
  const isCustomerAdmin = React.useMemo(() => currentRole?.code === "customer-admin", [ currentRole?.code ]);

  const onFetchData = React.useCallback((params: {filters: FilterParams<IShutdown>, pagination: PaginatedParams<IShutdown>}) => {
    const { filters, pagination } = params;
    const defaultFilters = { endDate: { $gte: moment.utc().subtract(1, "week") } };

    if (isCustomerAdmin) {
      getUserAccessibleShutdowns({
        pagination,
        filters: {
          ...defaultFilters,
          ...filters
        }
      });
    } else if (dropId) {
      getShutdowns({
        pagination,
        filters: {
          ...defaultFilters,
          ...filters
        },
        branchId: dropId
      });
    }
  }, [ dropId, getShutdowns ]);

  const editShutdownAction = React.useCallback((id: number) => {
    if (currentRole?.type === "admin") {
      if (match?.params.customerId && match.params.dropId) {
        history.push(`/admin/customers/${match.params.customerId}/drops/${match.params.dropId}/shutdowns/${id}`);
      }
    } else {
      history.push(`/shutdowns/${id}`);
    }
  }, [
    currentRole?.type,
    history,
    match?.params.customerId,
    match?.params.dropId
  ]);

  const createShutdownAction = React.useCallback((dropOverrideId?: number) => {
    if (currentRole?.type === "admin") {
      if (match?.params.customerId && match.params.dropId) {
        history.push(`/admin/customers/${match.params.customerId}/drops/${match.params.dropId}/shutdowns/new`);
      }
    } else if (isCustomerAdmin) {
      history.push(`/shutdowns/new/${dropOverrideId}`);
    } else {
      history.push("/shutdowns/new");
    }
  }, [
    currentRole?.type,
    history,
    isCustomerAdmin,
    match?.params.customerId,
    match?.params.dropId
  ]);

  const deleteShutdownAction = (id: number) => {
    setModalData(id);
  };

  const deleteShutdownActionConfirm = () => {
    if (modalData) {
      deleteShutdown(modalData);
      setModalData(null);
    }
  };

  // On holiday deletion
  React.useEffect(() => {
    if (deleteShutdownResponse.data) {
      toast.success("The holiday has been successfully deleted.");

      if (TableRef.current) {
        TableRef.current.reloadTable();
      }
    }

    if (deleteShutdownResponse.error) {
      toast.error(`Could not delete holiday - ${deleteShutdownResponse.error.message}`);
    }
  }, [ deleteShutdownResponse ]);

  /**
   * Table actions
   */
  const tableActions = React.useCallback(({ row }: CellContext<IShutdown, unknown>) => {
    const actions = [];

    if (canAmend) {
      actions.push({
        title: "Edit",
        action: () => editShutdownAction(row.original.id)
      });
    }

    if (canDelete) {
      actions.push({
        title: "Delete",
        action: () => deleteShutdownAction(row.original.id)
      });
    }

    return actions;
  }, [
    canAmend,
    canDelete,
    editShutdownAction
  ]);

  const tableColumns: Array<ColumnDef<IShutdown, keyof IShutdown>> = React.useMemo(() => {
    return shutdownsTableColumns(row => {
      const actions = tableActions(row);

      return actions.length ? (
        <ActionButton
          title="Actions"
          items={tableActions(row)}
          loading={deleteShutdownResponse.pending}
        />
      ) : null;
    },
    props.terminology,
    isCustomerAdmin

    ) ;
  }, [
    props.terminology,
    isCustomerAdmin,
    tableActions,
    deleteShutdownResponse.pending
  ]);

  return (
    <Layout
      {...props}
      title="Holidays"
    >
      <MainContentWrapper
        fill
        Header={canCreate && (
          <LikeToDo
            copy="What would you like to do?"
            buttons={[
              <CreateShutdownContext
                key="create-shutdown-button"
                createShutdown={dropOverride => {
                  createShutdownAction(dropOverride?.id);
                }}
                drop={isCustomerAdmin ? null : drop}
              />
            ]}
          />
        )}
      >
        <Table
          tableOf="Holidays"
          className="large"
          data={shutdownsData}
          onFetchData={onFetchData}
          defaultPagination={defaultPagination}
          defaultVisibility={{ id: false }}
          loading={shutdownsResponse.pending || userAccessibleShutdowns.pending}
          pageCount={shutdownsResponse.data?.pages || userAccessibleShutdowns.data?.pages}
          rowCount={shutdownsResponse.data?.count || userAccessibleShutdowns.data?.count}
          error={shutdownsResponse.error || userAccessibleShutdowns.error}
          columns={tableColumns}
          mainAction={row => editShutdownAction(row.original.id)}
        />
      </MainContentWrapper>

      <Modal
        small
        show={modalData !== null}
        type="confirm"
        title="Are you sure?"
        body="By deleting this holiday orders will now be allowed to be placed between the original holiday date range."
        backgroundAction={() => setModalData(null)}
        actions={[
          {
            name: "No, cancel",
            action: () => setModalData(null),
            $buttonType: "secondary"
          },
          {
            name: "I confirm",
            action: deleteShutdownActionConfirm,
            $buttonType: "primary"
          }
        ]}
      />
    </Layout>
  );
};

export default DropShutdownsPage;
